import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

interface Registration {
  id: number
  parent_name: string
  email: string
  mobile: string
  nok_name?: string
  nok_mobile?: string
  num_children: number
  reg_code: string
  created_at: string
}

const LookupPage: React.FC = () => {
  const { user } = useAuth()
  const [regCode, setRegCode] = useState('')
  const [registration, setRegistration] = useState<Registration | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setRegistration(null)

    try {
      const response = await fetch('/api/lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ regCode }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Lookup failed')
      }

      const data = await response.json()
      setRegistration(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lookup failed')
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    return (
      <div className="card">
        <div className="alert alert-error">
          Please log in to access the lookup tool.
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1>Registration Lookup</h1>
        <p>Enter a registration code to retrieve parent contact details.</p>
      </div>

      <div className="card mb-8" style={{ maxWidth: '500px' }}>
        <form onSubmit={handleLookup}>
          <div className="form-group">
            <label htmlFor="regCode" className="form-label">
              Registration Code
            </label>
            <input
              type="text"
              id="regCode"
              className="form-input"
              value={regCode}
              onChange={(e) => setRegCode(e.target.value)}
              required
              disabled={loading}
              placeholder="Enter registration code"
            />
          </div>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
            style={{ width: '100%' }}
          >
            {loading ? <span className="spinner" /> : 'Lookup'}
          </button>
        </form>

        {error && (
          <div className="alert alert-error mt-4">
            {error}
          </div>
        )}
      </div>

      {registration && (
        <div className="card">
          <h2 className="mb-4">Registration Details</h2>
          <div className="space-y-2">
            <p><strong>Registration Code:</strong> {registration.reg_code}</p>
            <p><strong>Parent Name:</strong> {registration.parent_name}</p>
            <p><strong>Email:</strong> {registration.email}</p>
            <p><strong>Mobile:</strong> {registration.mobile}</p>
            <p><strong>Number of Children:</strong> {registration.num_children}</p>
            {registration.nok_name && (
              <p><strong>Next of Kin:</strong> {registration.nok_name}</p>
            )}
            {registration.nok_mobile && (
              <p><strong>NOK Mobile:</strong> {registration.nok_mobile}</p>
            )}
            <p><strong>Registered:</strong> {new Date(registration.created_at).toLocaleString()}</p>
          </div>
          
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> This information is for emergency use only. 
              Contact the parent immediately if a child is missing or needs assistance.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default LookupPage
